import { useState, useEffect, useMemo, useCallback } from "react"
import toast from "react-hot-toast"
import { lumi } from "../lib/lumi"
import { useAuth } from "../hooks/useAuth"
import {
  useMilestones,
  Milestone,
  MilestoneInput,
  MilestoneStatus,
  STATUS_META,
  KANBAN_COLUMNS,
} from "../hooks/useMilestones"
import { MilestoneGantt } from "./MilestoneGantt"
import { MilestoneKanban } from "./MilestoneKanban"
import { MilestoneFormModal } from "./MilestoneFormModal"

interface ProjectOption {
  _id: string
  projectName: string
}

type ViewMode = "gantt" | "kanban"

export function ScheduleManagement() {
  const auth = useAuth()
  const {
    milestones,
    loading,
    fetchMilestones,
    createMilestone,
    updateMilestone,
    deleteMilestone,
    toggleTodo,
  } = useMilestones()

  const [projects, setProjects] = useState<ProjectOption[]>([])
  const [projectFilter, setProjectFilter] = useState<string>("all")
  const [viewMode, setViewMode] = useState<ViewMode>("gantt")
  const [showArchive, setShowArchive] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Milestone | null>(null)

  // 마일스톤 등록/수정/상태 변경은 모든 로그인 사용자가 가능
  const myUserId = auth?.user?.userId || ""
  const myUserName = auth?.userName || ""
  const canManage = Boolean(auth?.user)

  // 삭제는 본인이 등록한 마일스톤만 가능 (관리자는 예외적으로 전체 삭제 허용)
  const canDeleteMilestone = useCallback(
    (m: Milestone | null) => {
      if (!m) return false
      if (auth?.isAdmin) return true
      if (myUserId && m.creator && m.creator === myUserId) return true
      // 레거시 데이터: creator가 비어 있고 작성자명만 저장된 경우 보조 매칭
      if (!m.creator && myUserName && m.creatorName === myUserName) return true
      return false
    },
    [auth?.isAdmin, myUserId, myUserName]
  )

  // 프로젝트 목록 + 마일스톤 병렬 로드
  useEffect(() => {
    let mounted = true
    const load = async () => {
      try {
        const [projectRes] = await Promise.all([
          lumi.entities.projects.list({
            filter: { status: "active" },
            projection: { _id: 1, projectName: 1 },
            sort: { projectName: 1 },
          }),
          fetchMilestones(),
        ])
        if (mounted) {
          setProjects(((projectRes?.list || []) as ProjectOption[]).filter((p) => p?.projectName))
        }
      } catch (error) {
        console.error("프로젝트 목록 조회 실패:", error)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [fetchMilestones])

  // 필터링된 마일스톤
  const filtered = useMemo(() => {
    let list = milestones
    if (projectFilter !== "all") {
      list = list.filter((m) => m.projectId === projectFilter)
    }
    if (!showArchive) {
      list = list.filter((m) => m.status !== "archive")
    }
    return list
  }, [milestones, projectFilter, showArchive])

  // 요약 통계
  const stats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10)
    const counts: Record<string, number> = {}
    KANBAN_COLUMNS.forEach((s) => {
      counts[s] = filtered.filter((m) => m.status === s).length
    })
    const overdue = filtered.filter(
      (m) => m.status !== "completed" && m.status !== "archive" && m.dueDate.slice(0, 10) < today
    ).length
    // 7일 내 임박
    const soonLimit = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10)
    const dueSoon = filtered.filter(
      (m) =>
        m.status !== "completed" &&
        m.status !== "archive" &&
        m.dueDate.slice(0, 10) >= today &&
        m.dueDate.slice(0, 10) <= soonLimit
    ).length
    return { counts, overdue, dueSoon, total: filtered.length }
  }, [filtered])

  const handleOpenCreate = () => {
    if (!canManage) {
      toast.error("마일스톤 등록은 로그인 후 가능합니다.")
      return
    }
    setEditTarget(null)
    setModalOpen(true)
  }

  const handleOpenEdit = useCallback(
    (m: Milestone) => {
      if (!canManage) {
        toast.error("마일스톤 수정은 로그인 후 가능합니다.")
        return
      }
      setEditTarget(m)
      setModalOpen(true)
    },
    [canManage]
  )

  const handleSubmit = async (input: MilestoneInput) => {
    if (editTarget) {
      await updateMilestone(editTarget._id, {
        ...input,
        completedAt:
          input.status === "completed" && editTarget.status !== "completed"
            ? new Date().toISOString()
            : editTarget.completedAt,
      })
      toast.success("마일스톤이 수정되었습니다.")
    } else {
      await createMilestone(input, auth?.user?.userId || "", auth?.userName || "")
      toast.success("마일스톤이 추가되었습니다.")
    }
    await fetchMilestones()
  }

  // 칸반 드래그로 상태 변경
  const handleStatusChange = async (m: Milestone, status: MilestoneStatus) => {
    if (!canManage) {
      toast.error("상태 변경은 로그인 후 가능합니다.")
      return
    }
    try {
      await updateMilestone(m._id, {
        status,
        completedAt: status === "completed" ? new Date().toISOString() : undefined,
      })
      await fetchMilestones()
      toast.success(`"${m.title}" → ${STATUS_META[status].label}`)
    } catch (error) {
      console.error("상태 변경 실패:", error)
      toast.error("상태 변경에 실패했습니다.")
    }
  }

  // 칸반 카드에서 세부 To do 체크 토글 (낙관적 반영 + 즉시 저장)
  const handleToggleTodo = useCallback(
    async (m: Milestone, todoId: string) => {
      try {
        await toggleTodo(m, todoId, auth?.userName || "")
      } catch (error) {
        console.error("To do 저장 실패:", error)
        toast.error("To do 저장에 실패했습니다.")
      }
    },
    [toggleTodo, auth?.userName]
  )

  const handleDelete = async () => {
    if (!editTarget) return
    if (!canDeleteMilestone(editTarget)) {
      toast.error("본인이 등록한 마일스톤만 삭제할 수 있습니다.")
      return
    }
    if (!window.confirm(`"${editTarget.title}" 마일스톤을 삭제하시겠습니까?`)) return
    try {
      await deleteMilestone(editTarget._id)
      setModalOpen(false)
      setEditTarget(null)
      await fetchMilestones()
      toast.success("마일스톤이 삭제되었습니다.")
    } catch (error) {
      console.error("삭제 실패:", error)
      toast.error("삭제에 실패했습니다.")
    }
  }

  return (
    <div className="min-h-screen bg-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* 헤더 */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">일정 관리</h1>
              <p className="mt-1 text-sm text-gray-600">
                프로젝트별 단계 마일스톤과 산출물을 타임라인·보드로 관리합니다.
              </p>
            </div>
            <button
              onClick={handleOpenCreate}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg shadow-sm transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              마일스톤 추가
            </button>
          </div>
        </div>

        {/* 요약 카드 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-semibold text-gray-500 mb-1">전체 마일스톤</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-semibold text-gray-500 mb-1">진행 중</p>
            <p className="text-2xl font-bold text-orange-600">{stats.counts.in_progress || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-semibold text-gray-500 mb-1">7일 내 임박</p>
            <p className="text-2xl font-bold text-amber-600">{stats.dueSoon}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-semibold text-gray-500 mb-1">지연</p>
            <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
          </div>
        </div>

        {/* 컨트롤 바 */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-5">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* 뷰 전환 */}
            <div className="inline-flex bg-gray-100 rounded-lg p-1 self-start">
              <button
                onClick={() => setViewMode("gantt")}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-semibold transition-colors ${
                  viewMode === "gantt" ? "bg-white text-orange-700 shadow-sm" : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h10M4 12h16M4 18h7" />
                </svg>
                간트 차트
              </button>
              <button
                onClick={() => setViewMode("kanban")}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-semibold transition-colors ${
                  viewMode === "kanban" ? "bg-white text-orange-700 shadow-sm" : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v6a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z" />
                </svg>
                칸반 보드
              </button>
            </div>

            <div className="flex-1" />

            {/* 프로젝트 필터 */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              >
                <option value="all">전체 프로젝트</option>
                {projects.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.projectName}
                  </option>
                ))}
              </select>

              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={showArchive}
                  onChange={(e) => setShowArchive(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                Archive 포함
              </label>
            </div>
          </div>
        </div>

        {/* 본문 */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 py-20 text-center">
            <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500">일정을 불러오는 중...</p>
          </div>
        ) : viewMode === "gantt" ? (
          <MilestoneGantt milestones={filtered} onEdit={handleOpenEdit} />
        ) : (
          <MilestoneKanban
            milestones={filtered}
            onEdit={handleOpenEdit}
            onStatusChange={handleStatusChange}
            onToggleTodo={handleToggleTodo}
          />
        )}

        {/* 안내 */}
        <p className="mt-4 text-xs text-gray-500">
          * 마일스톤 등록·수정은 모든 사용자가 가능하며, 삭제는 본인이 등록한 마일스톤만 가능합니다.
        </p>
        {viewMode === "kanban" ? (
          <p className="mt-1 text-xs text-gray-500">
            * 카드를 드래그해 다른 컬럼으로 옮기면 상태가 변경되고, To do 항목을 체크하면 즉시 저장됩니다.
          </p>
        ) : null}
      </div>

      {/* 마일스톤 등록/수정 모달 */}
      <MilestoneFormModal
        open={modalOpen}
        editTarget={editTarget}
        projects={projects}
        defaultProjectId={projectFilter !== "all" ? projectFilter : undefined}
        onClose={() => {
          setModalOpen(false)
          setEditTarget(null)
        }}
        onSubmit={handleSubmit}
      />

      {/* 수정 모드에서 삭제 버튼 (모달 외부 하단 고정 대신 모달 내 별도 처리) */}
      {modalOpen && editTarget && canDeleteMilestone(editTarget) ? (
        <div className="fixed bottom-6 left-6 z-[60]">
          <button
            onClick={handleDelete}
            className="px-4 py-2.5 bg-white border border-red-300 text-red-600 hover:bg-red-50 font-semibold rounded-lg shadow-lg transition-colors text-sm"
          >
            마일스톤 삭제
          </button>
        </div>
      ) : null}
    </div>
  )
}
